import ReactGA from "react-ga4";
import { deleteNoteAction, deleteGroupNoteAction } from "store/actions/notes";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useFetchNotes, useLogout, useCurrentUser } from "utils/hooks";
import { Note } from "types";

interface DeleteNoteArgs {
  note: Note;
}

const useDeleteNote = () => {
  const dispatch = useDispatch();
  const { logOut } = useLogout();
  const { fetchNotes, fetchGroupNotes } = useFetchNotes();
  const { user } = useCurrentUser();

  const deleteNote = ({ note }: DeleteNoteArgs) => {
    if (!note) {
      return null;
    }

    return new Promise(() => {
      setTimeout(() => {
        fetch(`${process.env.REACT_APP_API_URL}`, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            query: `
            mutation {
              deleteNote(uuid: "${note.uuid}")
            }
          `,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            organizationId: user?.activeOrganizationId || "NA",
            permissionKey: "delete_notes",
          },
        })
          .then(async (res) => {
            if (res.status !== 200 && res.status !== 201) {
              const error = await res.json();
              if (error.errors[0].message.includes("BRKN_6001")) {
                toast.warn(`For security purposes please log in again.`);
                logOut();
              } else {
                toast.error(`Hmm, something went wrong.`);
                throw new Error("Failed!");
              }
            }
            return res.json();
          })
          .then((resData) => {
            if (resData.errors) {
              toast.error(
                `Hmm, something went wrong. ${resData.errors[0].message}`
              );
            } else {
              return dispatch(deleteNoteAction(resData.data.deleteNote));
            }
          })
          .then((res) => {
            if (res) {
              fetchNotes();
              ReactGA.event("note_removed", {
                user_id: user?.id,
                note_id: note.uuid,
              });
              toast(`Note deleted :)`);
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }, 100);
    });
  };

  const deleteGroupNote = ({ note }: DeleteNoteArgs) => {
    if (!note) {
      return null;
    }

    return new Promise(() => {
      setTimeout(() => {
        fetch(`${process.env.REACT_APP_API_URL}`, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            query: `
            mutation {
              deleteNote(uuid: "${note.uuid}")
            }
          `,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            organizationId: user?.activeOrganizationId || "NA",
            permissionKey: "delete_notes",
          },
        })
          .then(async (res) => {
            if (res.status !== 200 && res.status !== 201) {
              const error = await res.json();
              if (error.errors[0].message.includes("BRKN_6001")) {
                toast.warn(`For security purposes please log in again.`);
                logOut();
              } else {
                toast.error(`Hmm, something went wrong.`);
                throw new Error("Failed!");
              }
            }
            return res.json();
          })
          .then((resData) => {
            if (resData.errors) {
              toast.error(
                `Hmm, something went wrong. ${resData.errors[0].message}`
              );
            } else {
              return dispatch(deleteGroupNoteAction(resData.data.deleteNote));
            }
          })
          .then((res) => {
            if (res) {
              note.groupId && fetchGroupNotes(note.groupId);
              ReactGA.event("note_removed", {
                user_id: user?.id,
                note_id: note.uuid,
              });
              toast(`Note deleted :)`);
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }, 100);
    });
  };

  return {
    deleteNote,
    deleteGroupNote,
  };
};

export default useDeleteNote;
